/* eslint-disable camelcase */
import {
  getCurrentInstance,
  ComponentInternalInstance,
  ref,
  computed,
  Ref,
  UnwrapRef,
  reactive,
  toRef,
  watch,
  ComputedRef,
} from '@vue/composition-api';
import dayjs from 'dayjs';
import _ from 'lodash';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入 compositions
import historyHandler from '@/renderer/components/SpacePage/compositions/historyHandler';
import guideStepHandler from '@/renderer/components/GuideAndHelp/composition/guideStepHandler';

// 引入工具类
import { PAY_SOURCE, openTeamPayDialog } from '@/renderer/scripts/composition/dialogHandler';
import { can } from '@/renderer/scripts/composition/authHandler';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import ipc from '@/renderer/scripts/utils/ipc';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools/index';
import { httpUrl } from '@/renderer/scripts/http';
import { checkVirusStatus } from '@/renderer/scripts/utils/checkVirusStatus';
import store from '@/renderer/store';
import { getCustomDomainOrWebUrl } from '@/renderer/scripts/utils/CustomDomain/customDomain';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import { windowEnvs } from '@/renderer/scripts/utils/ConstantConfig/PrivateConfig';

interface ShareItem extends Partial<SMH.SpaceFileItemData>, Partial<SMH.ShareLinkFilesInfo> {
  type: SMH.FileItemBaseType | 'multi-file' | string;
  userName?: string;
  new?: boolean;
  versionId?: number;
  originItem?: any;
  canModify?: boolean;
  spaceOrgId?: number;
  user?: SMH.UserInfoBaseData;
}

type Handler = {
  show: Ref<boolean>;
  showWemeetShare: Ref<boolean>;
  linkData: UnwrapRef<ShareItem>;
  viewData: UnwrapRef<any>;
  linkSetShow: Ref<boolean>;
  extractionCodeCheck: Ref<boolean>;
  extractionCodeFormat: Ref<boolean>;
  enabelWatermarkText: ComputedRef<boolean>;
  watermarkTextFormat: ComputedRef<boolean>;
  formData: UnwrapRef<SMH.GetShareLinkParams>;
  expireDay: Ref<number>;
  expireDayShow: Ref<boolean>;
  linkUrl: Ref<string>;
  linkCode: Ref<string>;
  copyValue: Ref<string>;
  isCreate: Ref<boolean>;
  isChange: Ref<boolean>;
  isLoading: Ref<boolean>;
  showModify: Ref<boolean>;
  allowEmptyWatermarkText: boolean;
  methods: {
    openShare: (item: ShareItem[], openWemeetShare?: boolean) => void;
    viewShare: (item: ShareItem[]) => void;
    gotoLink: () => void;
    createLink: () => void;
    copyLink: () => void;
    reset: () => void;
    init: () => void;
    codeChange: (
      checked: boolean,
      context: {
        e: Event;
      },
    ) => void;
    handleChange: (val?: string, checked?: boolean) => void;
    dateDiff: (sDate1: string) => void;
    getReportParams: () => any;
  };
};

const get7Day = () => {
  const time = dayjs().add(7, 'days').format('YYYY-MM-DD HH:mm:ss');
  return time;
};
const get4Code = () => Math.random().toString(36).slice(2, 6);
export default function handler(spaceId?: Ref<SpaceId>): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const router = vm.$router;
  const { props } = currComponent;
  const maxWatermarkTextLength = ConstantConfig.nicknameMaxLength + 5;
  const { historyConfig, methods: historyMethods } = historyHandler();
  const { methods: guideStepMethods } = guideStepHandler();
  const linkSetShow = ref(true); // 分享设置界面
  const extractionCodeCheck = ref(true); // 是否选中提取码
  const extractionCodeFormat = ref(true); // 提取码格式是否正确
  const expireDay = ref(7);
  const expireDayShow = ref(true);
  const linkUrlCode = ref('');
  const domain = ref(''); // webUrl或者自定义域名
  const linkCode = ref('');
  const copyValue = ref('');
  const isChange = ref(false);
  const isLoading = ref(false);
  const showModify = ref(true);
  let isUpdateSuc = false; // 链接更新标识，分享界面关闭后需刷新列表
  let originData = {};
  const propsLinkData: any = props?.linkData ? toRef(props, 'linkData') : undefined; // 创建时的数据
  const propsViewData: any = props?.viewData ? toRef(props, 'viewData') : undefined; // 查看时的数据
  const isCreate = computed(() => propsLinkData?.value);
  const privateLinkUrl = ref('');
  const { allowEmptyWatermarkText = false } = windowEnvs;
  const linkUrl = computed(() => {
    // 私有化没有自定义域名
    const linkUrl = vm.$store.getters.isPrivate ? privateLinkUrl.value : domain.value;
    return `${linkUrl}/web/share/${encodeURIComponent(linkUrlCode.value)}`;
  });
  const formData = reactive<Handler['formData']>({
    name: '',
    directoryInfoList: propsLinkData?.value?.directoryInfoList,
    expireTime: '',
    // isCreate.value ? get7Day() : moment(propsViewData.expireTime).format('YYYY-MM-DD'),
    isPermanent: false,
    extractionCode: '',
    linkToLatestVersion: true,
    canPreview: true,
    canModify: false,
    canDownload: false,
    canSaveToNetDisc: false,
    previewCount: undefined,
    downloadCount: undefined,
    shareTraffic: 0,
    watermarkText: undefined,
  });
  const enabelWatermarkText = computed(
    () =>
      store.getters.shareWatermarkConfig.enableShareWatermark &&
      store.getters.shareWatermarkConfig.shareWatermarkType === 3,
  );
  const watermarkTextFormat = computed(() => {
    if (enabelWatermarkText.value) {
      if (
        formData.watermarkText &&
        formData.watermarkText.length > 0 &&
        formData.watermarkText.length <= maxWatermarkTextLength
      ) {
        return true;
      }
      // 20250402 交大改动：允许自定义水印为空
      if (allowEmptyWatermarkText && !formData.watermarkText) {
        return true;
      }
      return false;
    }
    return true;
  });
  const show = ref(false);
  const showWemeetShare = ref(false);
  let guideSuccess = false;
  const linkData = reactive<ShareItem>({
    name: '',
    type: SMH.FileItemBaseType.file,
    creationTime: '',
    userNickname: '',
    userId: '',
    directoryInfoList: [],
    item: {},
    canModify: true,
    path: [],
  });
  const viewData = ref({});
  const methods = {
    async openShare(shareItems: Array<ShareItem>) {
      // 默认第一个（TODO，需要处理多选逻辑)
      const firstShareItem = shareItems[0];
      const params = shareItems.map(o => ({
        userId: Number(o.userId || o.user?.id),
        virusAuditStatus: o.virusAuditStatus as number,
        sensitiveWordAuditStatus: o.sensitiveWordAuditStatus as number,
      }));
      if (!checkVirusStatus(params, 'share')) return;
      linkData.name =
        shareItems.length > 1
          ? i18n_t('{slot1} 等{slot2}个', {
              slot1: firstShareItem.name,
              slot2: shareItems.length,
            })
          : firstShareItem.name;
      linkData.type = shareItems.length === 1 ? firstShareItem.type : 'multi-file';
      linkData.canModify = can('Modify', {
        ...firstShareItem,
        authorityList: firstShareItem.authorityList as SMH.AuthorityList,
      });
      try {
        const filePath = firstShareItem.path?.join('/') as string;
        const options = {
          path: filePath,
          spaceId: spaceId?.value,
          spaceOrgId: firstShareItem.spaceOrgId || vm.$store.getters.orgId,
        };
        const { data } = await smh.userAPI.getDirectoryOrFileInfo(options);
        linkData.userNickname = data.creationPerson as string;
      } catch (error: any) {
        tips.error(error);
      }
      linkData.creationTime = firstShareItem.creationTime;
      const dirTmp: Array<SMH.DirectoryInfo> = [];
      const SpaceId = spaceId?.value || (await spaceATM.getPersonalSpaceIdAsync());
      for (let index = 0; index < shareItems.length; index++) {
        const element = shareItems[index];
        dirTmp.push({
          spaceId: SpaceId,
          path: element.path?.join('/') as string,
          versionId: (element.versionId as number) || 0,
          type: element.type as string,
          spaceOrgId: element?.spaceOrgId || vm.$store.getters.orgId,
        });
      }
      linkData.directoryInfoList = dirTmp;
      if (vm.$store.getters.isInWeMeetApp && shareItems.length === 1 && firstShareItem.type !== 'dir') {
        // eslint-disable-next-line prefer-destructuring
        linkData.item = firstShareItem;
        // 腾讯会议中若分享的文件为单个且有预览或编辑其中之一的权限则打开过渡分享页
        if (linkData.item.canPreview || linkData.item.canEdit) {
          showWemeetShare.value = true;
        } else {
          show.value = true;
        }
      } else {
        show.value = true;
      }
    },
    // 查看分享和创建分享区分开
    viewShare(item: Array<ShareItem>) {
      [viewData.value] = item;
      show.value = true;
    },
    gotoLink() {
      vm.$emit('update:show', false);
      setTimeout(() => {
        dispatchRouter({ name: 'linkManage' });
      }, 0);
    },
    checkDateValidate(date: string) {
      if (dayjs(date).valueOf() < Date.now() && !formData.isPermanent) {
        tips.error(i18n_t('请输入正确的有效时间'), true);
        return false;
      }
      return true;
    },
    async createLink() {
      if (!this.checkDateValidate(formData.expireTime)) return;
      try {
        let res;
        if (isCreate.value) {
          if (!formData.extractionCode) delete formData.extractionCode;
          if (!formData.canPreview && !formData.canModify && !formData.canDownload && !formData.canSaveToNetDisc) {
            tips.warning(i18n_t('请进行访问权限控制'));
            return;
          }
          isLoading.value = true;
          res = await smh.userAPI.getShareLink(formData);
          // 创建外链时才进行上报，修改外链上报信息目前不全，暂不上报
          const reportParams = methods.getReportParams();
          reporter.report('share', {
            ...reportParams,
            operate: 'create_link',
          });
        } else {
          const tmp = {} as SMH.ShareLinkBaseInfo;
          if (!formData.isPermanent) {
            tmp.expireTime = formData.expireTime;
          }
          tmp.isPermanent = formData.isPermanent;
          tmp.extractionCode = formData.extractionCode;
          tmp.linkToLatestVersion = formData.linkToLatestVersion;
          tmp.canModify = formData.canModify;
          tmp.canSaveToNetDisc = formData.canSaveToNetDisc;
          tmp.canPreview = formData.canPreview;
          tmp.previewCount = formData.previewCount;
          tmp.canDownload = formData.canDownload;
          tmp.downloadCount = formData.downloadCount;
          tmp.watermarkText = formData.watermarkText;
          if (!formData.canPreview && !formData.canModify && !formData.canDownload && !formData.canSaveToNetDisc) {
            tips.warning(i18n_t('请进行访问权限控制'));
            return;
          }
          res = await smh.userAPI.updateSharedLink({
            shareId: propsViewData?.value.id,
            ...tmp,
          });
          isUpdateSuc = true;
        }
        linkSetShow.value = false;
        isChange.value = false;
        privateLinkUrl.value = res.data.endpoint || httpUrl.webUrl;
        linkUrlCode.value = res.data.code;
        domain.value = getCustomDomainOrWebUrl(res.data.domains);
        linkCode.value = formData.extractionCode as string;
        guideSuccess = true;
        methods.copyLink();
      } catch (error: any) {
        if (error?.code === 'OrganizationShareLinkLimitReached') {
          openTeamPayDialog(PAY_SOURCE.CREATE_SHARE_LINK);
          return;
        }
        tips.error(error as MsgError);
      } finally {
        isLoading.value = false;
      }
    },
    getReportParams() {
      // 上报
      const share_authority_preview = formData.canPreview ? 1 : 0;
      const share_authority_edit = formData.canModify ? 1 : 0;
      const share_authority_download = formData.canDownload ? 1 : 0;
      const share_authority_todisck = formData.canSaveToNetDisc ? 1 : 0;
      // eslint-disable-next-line no-nested-ternary
      const shareLinkNew = !historyConfig.enableFileHistory ? 2 : formData.linkToLatestVersion ? 1 : 0;
      const fileNameList = (formData.directoryInfoList || []).map(file => file.path);
      return {
        file_name: formData?.name || '',
        share_authority_preview,
        share_authority_edit,
        share_authority_download,
        share_authority_todisck,
        share_time: 'user-defined',
        share_password: extractionCodeCheck.value ? 1 : 0,
        share_link_new: shareLinkNew,
        share_way: 'copylink',
        share_mode: formData?.directoryInfoList?.length > 1 ? 'batch' : 'single',
        batch_num: formData?.directoryInfoList?.length,
        shareFileNameList: fileNameList,
      };
    },
    copyLink() {
      const linkName = i18n_t('分享内容: {slot1}', {
        slot1: propsLinkData?.value.name || (propsViewData && LinkNameFilter(propsViewData.value)),
      });
      const linkUrlInfo = linkCode.value
        ? i18n_t('链接: {slot1}, 提取码: {slot2}', {
            slot1: linkUrl.value,
            slot2: linkCode.value,
          })
        : `${linkUrl.value}`;

      copyValue.value = `${linkName}\n${linkUrlInfo}`;

      copyText2Clipboard(copyValue.value)
        .then(() => {
          tips.success(i18n_t('分享链接已复制到剪贴板'));
        })
        .catch(() => {
          tips.error(i18n_t('分享链接复制出错'));
        });
      if (isCreate.value) {
        const reportParams = methods.getReportParams();
        reporter.report('share', {
          ...reportParams,
          operate: 'create_link_copy',
        });
      }
    },
    reset() {
      linkSetShow.value = true;
      isUpdateSuc && ipc.emit(ipc.RENDERER_EVENTS_MAP.LINK_LIST_FRESH);
      isUpdateSuc = false;
      expireDayShow.value = true;
      if (guideSuccess && vm.$route.name === 'personalSpace') {
        // 查看新手引导是否有对应数据，如果没有，对其进行对应分享相关的更新
        guideStepMethods.monitoringStepsDone('personalSpace', 2);
      }
    },
    init() {
      if (isCreate.value) {
        formData.expireTime = get7Day();
        formData.isPermanent = false;
        formData.extractionCode = vm.$store.getters.isInWeMeetApp ? '' : get4Code();
        // 在腾讯会议中默认提取码不选中
        extractionCodeCheck.value = !vm.$store.getters.isInWeMeetApp;
        formData.name = propsLinkData?.value.name;
        formData.directoryInfoList = propsLinkData?.value.directoryInfoList;
        formData.canPreview = true;
        formData.canDownload = false;
        formData.previewCount = undefined;
        formData.downloadCount = undefined;
        formData.canSaveToNetDisc = false;
        formData.canModify = false;
        if (enabelWatermarkText.value) {
          formData.watermarkText = `${
            store.getters.userBaseInfo.nickname
          } ${store.getters.userBaseInfo.phoneNumber.substr(-4)}`;
        }
        showModify.value = propsLinkData?.value.canModify;
        methods.dateDiff(formData.expireTime);
      } else {
        if (!propsViewData?.value.isPermanent) {
          formData.expireTime = dayjs(propsViewData?.value.expireTime).format('YYYY-MM-DD HH:mm:ss');
        } else {
          formData.expireTime = get7Day();
        }
        formData.isPermanent = propsViewData?.value.isPermanent || false;
        formData.extractionCode = propsViewData?.value.extractionCode || '';
        extractionCodeCheck.value = !!propsViewData?.value.extractionCode;
        formData.canPreview = !!propsViewData?.value.canPreview;
        formData.previewCount = propsViewData?.value.previewCount;
        formData.canModify = !!propsViewData?.value.canModify;
        showModify.value = can('Modify', propsViewData?.value);
        formData.canDownload = !!propsViewData?.value.canDownload;
        formData.downloadCount = propsViewData?.value.downloadCount;
        formData.canSaveToNetDisc = !!propsViewData?.value.canSaveToNetDisc;
        formData.linkToLatestVersion = !!propsViewData?.value.linkToLatestVersion;
        if (enabelWatermarkText.value) {
          formData.watermarkText = propsViewData?.value.watermarkText;
        } else {
          formData.watermarkText = '';
        }
        linkUrlCode.value = propsViewData?.value.code;
        privateLinkUrl.value = propsViewData?.value.endpoint;
        linkCode.value = propsViewData?.value.extractionCode;
        domain.value = getCustomDomainOrWebUrl(propsViewData.value.domains);
        originData = _.cloneDeep(formData);
        isChange.value = false;
        methods.dateDiff(formData.expireTime);
      }
    },
    codeChange(
      checked: boolean,
      context: {
        e: Event;
      },
    ) {
      extractionCodeFormat.value = true;
      const tmp = isCreate.value ? get4Code() : propsViewData?.value.extractionCode || get4Code();
      formData.extractionCode = checked ? tmp : '';
      methods.handleChange('extractionCode', checked);
    },
    handleChange(val?: string, checked?: boolean) {
      // 提取码在选中时校验
      if (val === 'extractionCode' && checked) {
        const regx = /[A-Za-z0-9]{4}/;
        extractionCodeFormat.value = regx.test(formData.extractionCode as string);
      }
      // 判断保留最初的一份，比较有没有变化
      isChange.value = !_.isEqual(originData, formData);
    },
    dateDiff(sDate1: string) {
      methods.handleChange();
      !dayjs(sDate1).isSame(get7Day(), 'seconds') && (expireDayShow.value = false); // 只在创建时选择默认，展示7天时间，改变后不展示
    },
  };

  watch(
    () => [formData.previewCount, formData.downloadCount],
    newValue => {
      isChange.value = !_.isEqual(originData, newValue);
    },
  );

  watch(
    () => formData.canPreview,
    val => {
      if (!val) {
        // 关闭历史版本
        formData.canModify = false;
      }
    },
  );
  return {
    linkSetShow,
    extractionCodeCheck,
    extractionCodeFormat,
    enabelWatermarkText,
    watermarkTextFormat,
    formData,
    expireDay,
    expireDayShow,
    linkUrl,
    linkCode,
    copyValue,
    isCreate,
    isChange,
    show,
    showWemeetShare,
    linkData,
    viewData,
    methods,
    isLoading,
    showModify,
    allowEmptyWatermarkText,
  };
}

export const LinkNameFilter = (item: { fileNum?: number; fileName?: string; name?: string }) => {
  if (!item.fileNum) return item.name;
  if (item.fileNum === 1) {
    return item.fileName;
  }
  return i18n_t('{slot1} 等{slot2}个', { slot1: item.fileName, slot2: item.fileNum });
};
